import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DownloadOutlined, UploadOutlined } from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Button,
  Collapse,
  DatePicker,
  Popconfirm,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
  Upload,
  message,
} from 'antd';
import dayjs from 'dayjs';
import { API } from '@/services/api.service';
import HowToCard from '../HowToCard';
import { useGoToSection } from '../howToLinks';
import { errorText, formatAmount, saveBlob } from '../format';
import CashFlowImportModal from './CashFlowImportModal';
import CashFlowSettings from './CashFlowSettings';

const { Text } = Typography;

const CASH_FLOW_KEYS = [
  'opsIn',
  'groupIn',
  'opsOut',
  'groupOut',
  'finIn',
  'finOut',
  'tax',
];

const INFLOWS = ['opsIn', 'groupIn', 'finIn'];

const netOf = (v) =>
  CASH_FLOW_KEYS.reduce(
    (sum, k) => sum + (INFLOWS.includes(k) ? 1 : -1) * (v[k] || 0),
    0
  );

/**
 * The Telekom group cash flow of one company: months already sent to the group
 * stay as they were sent, the following ones come from the bank postings.
 *
 * @param {Object} props - Component props
 * @param {string} props.companyId - Company whose ledger is used
 * @returns {JSX.Element} The panel
 */
export default function CashFlowPanel({ companyId }) {
  const { t } = useTranslation('clientReports');
  const queryClient = useQueryClient();
  // The form goes out after the month closes, so last month is the default.
  const [period, setPeriod] = useState(
    dayjs().subtract(1, 'month').startOf('month')
  );
  const [importFile, setImportFile] = useState(null);
  const year = period.year();
  const month = period.month() + 1;

  const { data, isLoading, error } = useQuery({
    queryKey: ['client-reports-cash-flow', companyId, year, month],
    queryFn: () => API.clientReports.getCashFlow(companyId, { year, month }),
    retry: false,
  });
  const report = data?.data;
  const [openSections, setOpenSections] = useState([]);
  useGoToSection('cashFlow', (key) => setOpenSections((keys) => [...new Set([...keys, key])]));

  const refresh = () => {
    queryClient.invalidateQueries({
      queryKey: ['client-reports-cash-flow', companyId],
    });
    queryClient.invalidateQueries({
      queryKey: ['client-reports-cash-flow-config', companyId],
    });
  };

  const exportMutation = useMutation({
    mutationFn: () =>
      API.clientReports.exportCashFlow(companyId, { year, month }),
    onSuccess: saveBlob,
    onError: async (e) => message.error(await errorText(e)),
  });

  const unfreezeMutation = useMutation({
    mutationFn: (m) =>
      API.clientReports.unfreezeCashFlowMonth(companyId, { year, month: m }),
    onSuccess: () => {
      message.success(t('cashFlow.unfrozen'));
      refresh();
    },
    onError: async (e) => message.error(await errorText(e)),
  });

  const rules = report?.rules;
  const lineLabel = (key) =>
    t(`cashFlow.lines.${key}`, {
      group: rules?.groupName || 'Telekom',
      owner: rules?.ownerName || t('cashFlow.ownerFallback'),
    });

  const rows = useMemo(() => {
    if (!report) return [];
    return [
      ...CASH_FLOW_KEYS.map((key) => ({ key, label: lineLabel(key) })),
      { key: 'net', label: t('cashFlow.net'), summary: true },
      { key: 'closing', label: t('cashFlow.closing'), summary: true },
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [report, t]);

  const cellFor = (row, mo) => {
    if (row.key === 'net') return <Text strong>{formatAmount(netOf(mo.values))}</Text>;
    if (row.key === 'closing') return <Text strong>{formatAmount(mo.closing)}</Text>;
    const value = mo.values[row.key];
    const ledger = mo.computed[row.key];
    const differs = mo.frozen && Math.abs(value - ledger) >= 0.01;
    const text = Math.abs(value) >= 0.005 ? formatAmount(value) : '';
    if (!differs) return text;
    return (
      <Tooltip title={t('cashFlow.ledgerValue', { amount: formatAmount(ledger) })}>
        <span style={{ borderBottom: '1px dashed #d48806' }}>{text || '0,00'}</span>
      </Tooltip>
    );
  };

  const columns = useMemo(() => {
    if (!report) return [];
    return [
      {
        title: t('cashFlow.line'),
        dataIndex: 'label',
        key: 'label',
        fixed: 'left',
        width: 300,
        render: (label, row) => (row.summary ? <Text strong>{label}</Text> : label),
      },
      ...report.months.map((mo) => ({
        key: `m${mo.month}`,
        align: 'right',
        width: 150,
        title: (
          <Space direction="vertical" size={2} style={{ alignItems: 'flex-end' }}>
            <span>{`${String(mo.month).padStart(2, '0')}.${year}`}</span>
            {mo.frozen ? (
              <Space size={4}>
                <Tooltip
                  title={t('cashFlow.frozenAt', {
                    date: dayjs(mo.frozen.frozenAt).format('DD.MM.YYYY HH:mm'),
                  })}
                >
                  <Tag color={mo.frozen.source === 'upload' ? 'purple' : 'blue'} style={{ marginInlineEnd: 0 }}>
                    {t(`cashFlow.frozen.${mo.frozen.source}`)}
                  </Tag>
                </Tooltip>
                <Popconfirm
                  title={t('cashFlow.unfreezeConfirm')}
                  okText={t('cashFlow.unfreeze')}
                  cancelText={t('fccs.cancel')}
                  onConfirm={() => unfreezeMutation.mutate(mo.month)}
                >
                  <Button type="link" size="small" style={{ padding: 0, height: 'auto' }}>
                    {t('cashFlow.unfreeze')}
                  </Button>
                </Popconfirm>
              </Space>
            ) : (
              <Tag style={{ marginInlineEnd: 0 }}>{t('cashFlow.fromLedger')}</Tag>
            )}
          </Space>
        ),
        render: (_, row) => cellFor(row, mo),
      })),
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [report, t, year]);

  // 404 = nothing has arrived from AB for that year yet.
  const errorMessage =
    error?.response?.status === 404
      ? t('fccs.noLedger', { year })
      : error?.response?.data?.message || error?.message;

  return (
    <div>
      <HowToCard guide="cashFlow" open={report ? !report.configured : undefined} />
      <Space id="howto-cashFlow-toolbar" wrap style={{ display: 'flex', marginBottom: 12 }}>
        <DatePicker
          picker="month"
          allowClear={false}
          value={period}
          format="MM.YYYY"
          onChange={(value) => value && setPeriod(value.startOf('month'))}
        />
        <Button
          type="primary"
          className="btn-primary"
          icon={<DownloadOutlined />}
          disabled={!report}
          loading={exportMutation.isPending}
          onClick={() => exportMutation.mutate()}
        >
          {t('cashFlow.download')}
        </Button>
        <Upload
          accept=".xlsx"
          showUploadList={false}
          beforeUpload={(file) => {
            setImportFile(file);
            return false;
          }}
        >
          <Button shape="round" icon={<UploadOutlined />}>
            {t('cashFlow.importButton')}
          </Button>
        </Upload>
      </Space>

      <div style={{ fontSize: 12, opacity: 0.7, marginBottom: 8 }}>
        {t('cashFlow.hint')}
      </div>

      {errorMessage && (
        <Alert type="warning" showIcon style={{ marginBottom: 12 }} message={errorMessage} />
      )}
      {report && !report.configured && (
        <Alert type="info" showIcon style={{ marginBottom: 12 }} message={t('cashFlow.notConfigured')} />
      )}

      {report && (
        <>
          <Text type="secondary" style={{ display: 'block', marginBottom: 6 }}>
            {t('cashFlow.opening', {
              date: `31.12.${year - 1}`,
              amount: formatAmount(report.opening.amount),
            })}
            {' · '}
            {t(`cashFlow.openingSource.${report.opening.source}`)}
          </Text>
          <Table
            scroll={{ x: 'max-content' }}
            rowKey="key"
            size="small"
            pagination={false}
            loading={isLoading}
            columns={columns}
            dataSource={rows}
          />
        </>
      )}

      <Collapse
        style={{ marginTop: 16 }}
        activeKey={openSections}
        onChange={setOpenSections}
        items={[
          {
            key: 'settings',
            label: t('cashFlow.settingsTitle'),
            children: (
              <div id="howto-cashFlow-settings">
                <CashFlowSettings companyId={companyId} onSaved={refresh} />
              </div>
            ),
          },
        ]}
      />

      {importFile && (
        <CashFlowImportModal
          companyId={companyId}
          file={importFile}
          year={year}
          onClose={() => setImportFile(null)}
          onApplied={() => {
            setImportFile(null);
            refresh();
          }}
        />
      )}
    </div>
  );
}
